import { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { Layers, AlertTriangle, History, X, RotateCcw } from "lucide-react";
import { DiscordBadge } from "@/components/DiscordBadge";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";

const HEADERS = ["#", "Team", "W", "L", "PTS", ""];

function Table({ rows, canModerate, onDeduct, onHistory }) {
  return (
    <div className="border border-[#232330] overflow-x-auto">
      <table className="w-full min-w-[720px] text-sm">
        <thead>
          <tr className="border-b border-[#232330] bg-[#0a0a12]">
            {HEADERS.map((h) => (
              <th key={h} className="text-left font-mono text-[10px] uppercase tracking-[0.2em] text-gray-500 px-4 py-3">
                {h}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((t, i) => (
            <tr
              key={t.id}
              data-testid={`standing-row-${t.tag}`}
              className="border-b border-[#232330] hover:bg-black/40 transition-colors"
            >
              <td className="px-4 py-4 font-mono text-gray-500">{String(i + 1).padStart(2, "0")}</td>
              <td className="px-4 py-4">
                <div className="flex items-center gap-3">
                  <span className="inline-block w-1 h-6" style={{ backgroundColor: t.logo_color }} />
                  <div>
                    <div className="font-heading font-black uppercase tracking-tight flex items-center gap-2">
                      {t.name}
                      {t.discord_linked && (
                        <DiscordBadge handle={t.tag} testid={`standing-discord-${t.tag}`} />
                      )}
                    </div>
                    <div className="text-[10px] text-gray-500 font-mono tracking-widest">{t.tag}</div>
                  </div>
                </div>
              </td>
              <td className="px-4 py-4 font-mono text-white">{t.wins || 0}</td>
              <td className="px-4 py-4 font-mono text-gray-400">{t.losses || 0}</td>
              <td className="px-4 py-4">
                <div className="flex items-center gap-2">
                  <span className="font-mono font-bold text-cyan-400 text-base">{t.points || 0}</span>
                  {t.deductions_total > 0 && (
                    <button
                      type="button"
                      onClick={() => onHistory && onHistory(t)}
                      data-testid={`standing-deduction-badge-${t.tag}`}
                      title="View deduction history"
                      className="inline-flex items-center gap-1 border border-[#FF4D4F]/50 text-[#FF4D4F] text-[9px] font-mono uppercase tracking-widest px-1.5 py-0.5 hover:bg-[#FF4D4F]/10 transition-colors cursor-pointer"
                    >
                      <AlertTriangle className="w-3 h-3" />−{t.deductions_total}
                    </button>
                  )}
                </div>
              </td>
              <td className="px-4 py-4 text-right">
                {canModerate && (
                  <div className="flex items-center justify-end gap-1">
                    <button
                      type="button"
                      onClick={() => onDeduct(t)}
                      data-testid={`standing-deduct-btn-${t.tag}`}
                      title="Deduct points"
                      className="p-1.5 border border-[#232330] text-gray-500 hover:text-[#FF4D4F] hover:border-[#FF4D4F] transition-colors"
                    >
                      <AlertTriangle className="w-3.5 h-3.5" />
                    </button>
                    <button
                      type="button"
                      onClick={() => onHistory(t)}
                      data-testid={`standing-history-btn-${t.tag}`}
                      title="Deduction history"
                      className="p-1.5 border border-[#232330] text-gray-500 hover:text-cyan-400 hover:border-cyan-400 transition-colors"
                    >
                      <History className="w-3.5 h-3.5" />
                    </button>
                  </div>
                )}
              </td>
            </tr>
          ))}
          {rows.length === 0 && (
            <tr>
              <td colSpan={HEADERS.length} className="px-4 py-8 text-center text-gray-500 font-mono text-xs uppercase tracking-widest">
                No teams in this division yet.
              </td>
            </tr>
          )}
        </tbody>
      </table>
    </div>
  );
}

function DeductDialog({ team, open, onClose, onCreated }) {
  const [points, setPoints] = useState(3);
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (open) {
      setPoints(3);
      setReason("");
    }
  }, [open]);

  const submit = async () => {
    if (!reason.trim() || reason.trim().length < 3) {
      toast.error("Give a short reason (3+ chars) so this is auditable");
      return;
    }
    if (points < 1 || points > 20) {
      toast.error("Points must be 1-20");
      return;
    }
    setBusy(true);
    try {
      const { data } = await api.post(`/admin/teams/${team.id}/deductions`, {
        points,
        reason: reason.trim(),
      });
      toast.success(`Deducted ${points} pts from ${team.tag || team.name}. New total: ${data.team_points_after}`);
      onCreated && onCreated(data);
      onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to deduct");
    } finally {
      setBusy(false);
    }
  };

  if (!team) return null;
  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="bg-[#0a0a12] border-[#232330] rounded-none max-w-md" data-testid="deduct-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading uppercase tracking-tight text-xl flex items-center gap-2">
            <AlertTriangle className="w-5 h-5 text-[#FF4D4F]" />
            Deduct points · {team.tag || team.name}
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <div>
            <Label className="text-[10px] uppercase tracking-widest text-gray-500">Points to deduct</Label>
            <div className="flex items-center gap-2 mt-1">
              <Input
                data-testid="deduct-points-input"
                type="number"
                min={1}
                max={20}
                value={points}
                onChange={(e) => setPoints(parseInt(e.target.value || "0", 10))}
                className="rounded-none bg-[#05050A] border-[#232330] font-mono w-24"
              />
              <span className="text-[10px] font-mono text-gray-500 uppercase tracking-widest">of 1-20</span>
            </div>
          </div>
          <div>
            <Label className="text-[10px] uppercase tracking-widest text-gray-500">Reason (visible to the team)</Label>
            <textarea
              data-testid="deduct-reason-input"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="e.g. Late to kickoff by 20 minutes · ignored referee calls · roster not locked by deadline"
              className="w-full h-24 rounded-none bg-[#05050A] border border-[#232330] mt-1 p-2 font-mono text-[11px] leading-relaxed"
            />
          </div>
          <div className="text-[10px] font-mono text-[#FF4D4F]/80 border border-[#FF4D4F]/30 bg-[#FF4D4F]/5 p-2">
            This will immediately subtract from the standings and post an embed to Discord. You can revoke it later from the deduction history.
          </div>
          <div className="flex items-center justify-end gap-2">
            <Button
              variant="outline"
              onClick={onClose}
              disabled={busy}
              className="rounded-none border-[#232330] text-gray-400 uppercase tracking-widest text-xs"
              data-testid="deduct-cancel"
            >
              Cancel
            </Button>
            <Button
              onClick={submit}
              disabled={busy || !reason.trim()}
              data-testid="deduct-submit"
              className="rounded-none bg-[#FF4D4F] hover:bg-red-500 text-white uppercase tracking-widest text-xs font-bold"
            >
              {busy ? "Deducting…" : `Deduct ${points} pts`}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function HistoryDialog({ team, open, onClose, onChanged, canModerate }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [activeTotal, setActiveTotal] = useState(0);

  const load = async () => {
    if (!team) return;
    setLoading(true);
    try {
      const { data } = await api.get(`/admin/teams/${team.id}/deductions`);
      setRows(data.deductions || []);
      setActiveTotal(data.active_total || 0);
    } catch (e) {
      // History fetch requires admin/official — non-mods just see the empty state.
      setRows([]);
      setActiveTotal(0);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (open) load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, team?.id]);

  const revoke = async (id) => {
    try {
      await api.post(`/admin/deductions/${id}/revoke`);
      toast.success("Deduction revoked · points refunded");
      await load();
      onChanged && onChanged();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Revoke failed");
    }
  };

  if (!team) return null;
  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="bg-[#0a0a12] border-[#232330] rounded-none max-w-2xl" data-testid="deduct-history-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading uppercase tracking-tight text-xl flex items-center gap-2">
            <History className="w-5 h-5 text-cyan-400" />
            Deduction history · {team.tag || team.name}
            {activeTotal > 0 && (
              <span className="text-[10px] font-mono text-[#FF4D4F] border border-[#FF4D4F]/50 px-1.5 py-0.5 ml-2">
                −{activeTotal} ACTIVE
              </span>
            )}
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-2 max-h-[480px] overflow-y-auto">
          {loading && <div className="text-xs text-gray-500 font-mono">Loading…</div>}
          {!loading && rows.length === 0 && (
            <div className="text-xs text-gray-500 font-mono text-center py-8 border border-[#232330]">
              {canModerate ? "No deductions on record — this team has a clean sheet." : "You need admin access to view deduction history."}
            </div>
          )}
          {rows.map((d) => (
            <div
              key={d.id}
              data-testid={`deduction-row-${d.id}`}
              className={`border p-3 ${d.revoked ? "border-[#232330] opacity-50" : "border-[#FF4D4F]/40 bg-[#FF4D4F]/5"}`}
            >
              <div className="flex items-start justify-between gap-3">
                <div className="flex-1 min-w-0">
                  <div className="font-mono text-sm">
                    <span className={d.revoked ? "text-gray-500 line-through" : "text-[#FF4D4F] font-bold"}>
                      −{d.points} pts
                    </span>
                    {d.revoked && (
                      <span className="ml-2 text-[9px] uppercase tracking-widest text-cyan-400">REVOKED</span>
                    )}
                  </div>
                  <div className="text-xs text-gray-300 mt-1 whitespace-pre-wrap break-words">{d.reason}</div>
                  <div className="text-[9px] font-mono text-gray-500 mt-2 flex gap-3 flex-wrap">
                    <span>{new Date(d.imposed_at).toLocaleString()}</span>
                    <span>by {d.imposed_by || "league"}</span>
                    {d.revoked && d.revoked_at && (
                      <span>revoked {new Date(d.revoked_at).toLocaleString()} by {d.revoked_by}</span>
                    )}
                  </div>
                </div>
                {canModerate && !d.revoked && (
                  <button
                    type="button"
                    onClick={() => revoke(d.id)}
                    data-testid={`deduct-revoke-${d.id}`}
                    title="Revoke this deduction"
                    className="p-1.5 border border-[#232330] text-gray-400 hover:text-cyan-400 hover:border-cyan-400 transition-colors shrink-0"
                  >
                    <RotateCcw className="w-3.5 h-3.5" />
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
        <div className="flex items-center justify-end pt-2">
          <Button
            variant="outline"
            onClick={onClose}
            className="rounded-none border-[#232330] text-gray-400 uppercase tracking-widest text-xs"
            data-testid="deduct-history-close"
          >
            Close
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default function Standings() {
  const [standings, setStandings] = useState([]);
  const [divisions, setDivisions] = useState([]);
  const [activeDiv, setActiveDiv] = useState("all");
  const [deductTarget, setDeductTarget] = useState(null);
  const [historyTarget, setHistoryTarget] = useState(null);
  const { user } = useAuth();
  const canModerate = user?.role === "admin" || user?.role === "official";

  const reload = () => {
    api.get("/standings").then((r) => setStandings(r.data));
  };

  useEffect(() => {
    reload();
    api.get("/divisions").then((r) => setDivisions(r.data));
  }, []);

  const groups = useMemo(() => {
    const map = new Map();
    for (const t of standings) {
      const key = t.division_id || "unassigned";
      if (!map.has(key)) {
        map.set(key, {
          id: key,
          name: t.division_name || (key === "unassigned" ? "Unassigned" : "Division"),
          color: t.division_color || "#232330",
          tier: t.division_tier || null,
          rows: [],
        });
      }
      map.get(key).rows.push(t);
    }
    for (const d of divisions) {
      if (!map.has(d.id)) {
        map.set(d.id, { id: d.id, name: d.name, color: d.color, tier: d.tier, rows: [] });
      }
    }
    return Array.from(map.values()).sort((a, b) => {
      if (a.id === "unassigned") return 1;
      if (b.id === "unassigned") return -1;
      return (a.name || "").localeCompare(b.name || "");
    });
  }, [standings, divisions]);

  const visible = activeDiv === "all" ? groups : groups.filter((g) => g.id === activeDiv);

  return (
    <div className="space-y-8" data-testid="standings-root">
      <div>
        <div className="text-cyan-400 font-mono tracking-[0.3em] text-xs mb-2">/ CIRCUIT TABLE</div>
        <h1 className="font-heading text-4xl lg:text-5xl font-black tracking-tighter uppercase">Standings</h1>
        <p className="text-gray-400 mt-2 text-sm">Live table split by division · points = your score in the match (5-0, 4-1, 3-2). Red badges show active penalty deductions.</p>
      </div>

      {groups.length > 1 && (
        <div className="flex flex-wrap gap-2" data-testid="standings-division-tabs">
          <button
            onClick={() => setActiveDiv("all")}
            data-testid="std-tab-all"
            className={`px-3 py-1.5 border text-[10px] font-mono uppercase tracking-widest transition-colors ${
              activeDiv === "all"
                ? "border-cyan-400 text-cyan-400"
                : "border-[#232330] text-gray-500 hover:text-white"
            }`}
          >
            All divisions
          </button>
          {groups.map((g) => (
            <button
              key={g.id}
              onClick={() => setActiveDiv(g.id)}
              data-testid={`std-tab-${g.id}`}
              className={`px-3 py-1.5 border text-[10px] font-mono uppercase tracking-widest transition-colors ${
                activeDiv === g.id ? "text-black" : "text-gray-400 hover:text-white"
              }`}
              style={{
                backgroundColor: activeDiv === g.id ? g.color : "transparent",
                borderColor: activeDiv === g.id ? g.color : "#232330",
              }}
            >
              {g.name}
            </button>
          ))}
        </div>
      )}

      <div className="space-y-10">
        {visible.map((g) => (
          <section key={g.id} data-testid={`standings-division-${g.id}`} className="space-y-3">
            <div className="flex items-center gap-3">
              <span className="inline-block w-2 h-6" style={{ backgroundColor: g.color }} />
              <Layers className="w-4 h-4" style={{ color: g.color }} />
              <div className="font-heading text-2xl font-black uppercase tracking-tight">{g.name}</div>
              {g.tier && (
                <span className="text-[10px] font-mono uppercase tracking-widest text-gray-500 border border-[#232330] px-2 py-0.5">
                  {g.tier}
                </span>
              )}
              <span className="text-[10px] font-mono uppercase tracking-widest text-gray-500 ml-2">
                {g.rows.length} team{g.rows.length === 1 ? "" : "s"}
              </span>
            </div>
            <Table
              rows={g.rows}
              canModerate={canModerate}
              onDeduct={setDeductTarget}
              onHistory={setHistoryTarget}
            />
          </section>
        ))}
      </div>

      <DeductDialog
        team={deductTarget}
        open={!!deductTarget}
        onClose={() => setDeductTarget(null)}
        onCreated={reload}
      />
      <HistoryDialog
        team={historyTarget}
        open={!!historyTarget}
        onClose={() => setHistoryTarget(null)}
        onChanged={reload}
        canModerate={canModerate}
      />
    </div>
  );
}
